import { motion } from "framer-motion";
import { Sparkles, PartyPopper, Rocket, Zap, Calendar, Heart, ChevronRight } from "lucide-react";
import { BirthdayCelebration } from "@/components/shared/BirthdayCelebration";
import { useNavigate } from "react-router-dom";

/**
 * Contextual greeting that adapts to the user's birthday proximity.
 * States:
 *   day 0         — Full celebration
 *   1-3 days      — Urgent/electric excitement
 *   4-10 days     — Building excitement
 *   11-30 days    — Planning mode
 *   post-birthday — Claim window reminder
 */
export default function BirthdayGreeting({ days, userName, userColor, hasItinerary, totalSavings, collectionTempo, itineraryStats }) {
  const color = userColor || "#0ea5e9";
  const navigate = useNavigate();

  if (days == null) return null;

  // Helper: generate tempo-aware subtitle
  const getTempoMessage = () => {
    if (!collectionTempo || !hasItinerary || !itineraryStats) return null;
    const { claimed = 0, total = 0, remaining = 0 } = itineraryStats;
    if (collectionTempo === "birthday_month") {
      const dayOfMonth = Math.max(1, 30 - days);
      if (dayOfMonth <= 30) return `Day ${dayOfMonth} of your birthday month. ${claimed} collected, ${remaining} still waiting.`;
    }
    if (collectionTempo === "birthday_week") {
      const dayOfWeek = Math.max(1, 7 - days);
      if (dayOfWeek <= 7) return `Day ${dayOfWeek} of your birthday week. ${remaining > 0 ? `${remaining} stops left today.` : "All caught up!"}`;
    }
    if (collectionTempo === "one_big_day" && days === 1) {
      return `Tomorrow is your big day. ${total} stops ready to go.`;
    }
    if (collectionTempo === "one_big_day" && days === 0) {
      return `It's your big day! ${total} stops, $${totalSavings} in birthday deals.`;
    }
    return null;
  };

  const tempoMsg = getTempoMessage();

  // ═══ DAY 0 — Celebration ═══
  if (days === 0) {
    return (
      <div className="px-4 mb-4" data-testid="greeting-day0">
        <BirthdayCelebration name={userName} />
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-3xl p-6 text-center text-white shadow-2xl overflow-hidden relative"
          style={{ background: `linear-gradient(135deg, ${color}, #f472b6, #fbbf24)` }}
        >
          <div className="absolute inset-0 opacity-10">
            {[...Array(8)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-2 h-2 bg-white rounded-full"
                style={{ left: `${(i * 19 + 5) % 100}%`, top: `${(i * 23 + 10) % 100}%` }}
                animate={{ opacity: [0.2, 1, 0.2], scale: [0.5, 1.5, 0.5] }}
                transition={{ duration: 2 + (i % 3), repeat: Infinity, delay: i * 0.3 }}
              />
            ))}
          </div>
          <div className="relative z-10">
            <PartyPopper className="w-10 h-10 mx-auto mb-2 text-white/90" />
            <h2 className="text-2xl font-black">Happy Birthday, {userName}!</h2>
            <p className="text-sm text-white/70 mt-1">Your birthday deals are waiting</p>
            {totalSavings > 0 && (
              <p className="text-xs text-white/50 mt-2">Up to <strong className="text-white/90">${totalSavings}</strong> in birthday deals available today</p>
            )}
          </div>
        </motion.div>
      </div>
    );
  }

  // ═══ POST-BIRTHDAY (-1 to -7) ═══
  if (days < 0) {
    const remaining = 7 - Math.abs(days);
    if (remaining <= 0) return null;
    return (
      <div className="px-4 mb-4" data-testid="greeting-post-bday">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-2xl p-5 text-white shadow-lg"
          style={{ background: "linear-gradient(135deg, #f59e0b, #ef4444)" }}
        >
          <div className="flex items-center gap-2 mb-1">
            <Heart className="w-4 h-4 text-yellow-200" />
            <span className="text-[10px] font-bold text-white/70 tracking-widest uppercase">Birthday Window</span>
          </div>
          <h2 className="text-lg font-black">Hope you had an amazing day, {userName}!</h2>
          <p className="text-xs text-white/60 mt-1">
            You still have <strong className="text-white">{remaining} {remaining === 1 ? "day" : "days"}</strong> to claim your birthday deals
          </p>
        </motion.div>
      </div>
    );
  }

  // ═══ 1-3 DAYS — Electric ═══
  if (days <= 3) {
    return (
      <div className="px-4 mb-4" data-testid="greeting-urgent">
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-2xl p-5 text-white shadow-xl overflow-hidden relative"
          style={{ background: `linear-gradient(135deg, ${color}dd, ${color}99)` }}
        >
          <div className="absolute inset-0 opacity-10">
            {[...Array(5)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-1.5 h-1.5 bg-white rounded-full"
                style={{ left: `${(i * 25 + 10) % 100}%`, top: `${(i * 20 + 15) % 100}%` }}
                animate={{ opacity: [0.2, 0.8, 0.2] }}
                transition={{ duration: 2, repeat: Infinity, delay: i * 0.3 }}
              />
            ))}
          </div>
          <div className="relative z-10">
            <div className="flex items-center gap-2 mb-1">
              <Zap className="w-4 h-4 text-yellow-300" />
              <span className="text-[10px] font-bold text-white/70 tracking-widest uppercase">
                {days === 1 ? "Tomorrow!" : "Almost here!"}
              </span>
            </div>
            <h2 className="text-xl font-black">
              {days === 1 ? "Your birthday is TOMORROW" : `Just ${days} days to go!`}
            </h2>
            <p className="text-xs text-white/60 mt-1">
              {tempoMsg || (hasItinerary
                ? "Your plan is ready. Time for final checks!"
                : "Last chance to prep your birthday plan")}
            </p>
          </div>
        </motion.div>
      </div>
    );
  }

  // ═══ 4-10 DAYS — Excited ═══
  if (days <= 10) {
    return (
      <div className="px-4 mb-4" data-testid="greeting-excited">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-2xl p-5 text-white shadow-lg overflow-hidden relative"
          style={{ background: `linear-gradient(135deg, ${color}cc, ${color}88)` }}
        >
          <div className="relative z-10">
            <div className="flex items-center gap-2 mb-1">
              <Rocket className="w-4 h-4 text-white/70" />
              <span className="text-[10px] font-bold text-white/60 tracking-widest uppercase">Countdown</span>
            </div>
            <h2 className="text-lg font-black">{days} days until your birthday!</h2>
            <p className="text-xs text-white/60 mt-1">
              {tempoMsg || (hasItinerary
                ? "Review your plan and make final adjustments"
                : "Perfect time to build your birthday plan")}
            </p>
          </div>
        </motion.div>
      </div>
    );
  }

  // ═══ 11-30 DAYS — Planning mode ═══
  const planDest = hasItinerary ? "/my-itinerary" : "/my-deals";
  return (
    <div className="px-4 mb-4" data-testid="greeting-planning">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-2xl p-4 border shadow-sm cursor-pointer active:scale-[0.98] transition-transform"
        style={{ borderColor: `${color}30`, background: `linear-gradient(135deg, ${color}08, ${color}04)` }}
        onClick={() => navigate(planDest)}
        data-testid="greeting-planning-link"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${color}18` }}>
            <Calendar className="w-5 h-5" style={{ color }} />
          </div>
          <div className="flex-1">
            <h2 className="text-sm font-bold text-slate-800">Your birthday is in {days} days</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              {tempoMsg || (hasItinerary ? "Your plan is set. Review anytime." : "Start planning to maximize your birthday deals!")}
            </p>
          </div>
          <div className="text-right flex-shrink-0">
            <span className="text-2xl font-black" style={{ color }}>{days}</span>
            <span className="text-[10px] text-slate-400 block">days</span>
          </div>
          <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0" />
        </div>
      </motion.div>
    </div>
  );
}
